import React, { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import { Contact } from '../../../types/contact.types';
import { useAskRemiStore } from '../../../stores/useAskRemiStore';

interface AskRemiButtonProps {
  contact: Contact;
  onClick?: (contact: Contact) => void;
  expandedByDefault?: boolean;
  pulsing?: boolean;
  size?: 'sm' | 'normal';
  expandDirection?: 'left' | 'right';
  expandDirectionMobile?: 'left' | 'right';
}

const pulseAnimation = `
@keyframes askRemiPulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.08); }
  100% { transform: scale(1); }
}
`;

export const AskRemiButton: React.FC<AskRemiButtonProps> = ({
  contact,
  onClick,
  expandedByDefault = false,
  pulsing = false,
  size = 'normal',
  expandDirection = 'left',
  expandDirectionMobile,
}) => {
  const [isHovered, setIsHovered] = useState(expandedByDefault);
  const [isMobile, setIsMobile] = useState(false);
  const { openModal, sendMessage } = useAskRemiStore();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const actualExpandDirection =
    isMobile && expandDirectionMobile !== undefined ? expandDirectionMobile : expandDirection;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
    const label = fullName || contact.phoneNumber || contact.id;
    openModal();
    sendMessage(`Give me a complete summary of ${label} (contact_id: ${contact.id})`);
    if (onClick) onClick(contact);
  };

  const sm = size === 'sm';
  const iconCls = sm ? 'w-2.5 h-2.5 sm:w-3 sm:h-3' : 'w-3 h-3 sm:w-3.5 sm:h-3.5';
  const dotH = sm ? 'h-5 sm:h-6' : 'h-6 sm:h-7';
  const dotW = sm ? 'w-5 sm:w-6' : 'w-6 sm:w-7';
  const pillCls = 'summarize-pill shadow-sm';

  return (
    <>
      {pulsing && <style>{pulseAnimation}</style>}

      <div className={`relative ${dotW} ${dotH}`}>
        {/* Collapsed: icon-only circle */}
        <button
          className={`absolute ${dotW} ${dotH} rounded-full flex items-center justify-center cursor-pointer ${pillCls} transition-all duration-300`}
          onClick={handleClick}
          title="Summarize this contact"
          style={{
            opacity: isHovered ? 0 : 1,
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            zIndex: 10,
            ...(actualExpandDirection === 'right' ? { right: 0 } : { left: 0 }),
            top: 0,
            animation: pulsing && !isHovered ? 'askRemiPulse 2s infinite ease-in-out' : 'none',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Zap className={`${iconCls} flex-shrink-0`} />
        </button>

        {/* Expanded: pill with label */}
        <div
          className={`absolute ${dotH} rounded-full flex items-center px-2 sm:px-2.5 cursor-pointer ${pillCls} transition-all duration-300 ${
            actualExpandDirection === 'right' ? 'flex-row-reverse' : ''
          }`}
          style={{
            zIndex: 10,
            ...(actualExpandDirection === 'right' ? { right: 0 } : { left: 0 }),
            top: 0,
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'scaleX(1)' : 'scaleX(0.3)',
            transformOrigin: actualExpandDirection === 'right' ? 'right' : 'left',
            pointerEvents: isHovered ? 'auto' : 'none',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleClick}
        >
          <Zap className={`${iconCls} flex-shrink-0`} />
          <span
            className={`text-[11px] font-medium whitespace-nowrap ${
              actualExpandDirection === 'right' ? 'mr-1.5' : 'ml-1.5'
            }`}
            style={{
              opacity: isHovered ? 1 : 0,
              transform: isHovered
                ? 'translateX(0)'
                : `translateX(${actualExpandDirection === 'right' ? '8px' : '-8px'})`,
              transition: 'all 0.25s ease 0.08s',
            }}
          >
            Summarize
          </span>
        </div>
      </div>
    </>
  );
};

export default AskRemiButton;
